import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { HttpClient, HttpClientConfig } from "./types/http-types";
import { getHttpClientConfig } from "./config/http-config";
import { authInterceptor } from "./interceptors/auth-interceptor";
import { localeInterceptor } from "./interceptors/locale-interceptor";
import { errorInterceptor } from "./interceptors/error-interceptor";

/**
 * Base HTTP Client
 * Combines axios instance with all interceptors
 * Follows SRP: Single responsibility - HTTP communication
 * Follows SOC: Separation of concerns - interceptors are separate modules
 */
export class BaseHttpClient implements HttpClient {
  protected axiosInstance: AxiosInstance;

  constructor(config?: Partial<HttpClientConfig>) {
    const finalConfig = {
      ...getHttpClientConfig(),
      ...config,
    };

    this.axiosInstance = axios.create(finalConfig);
    this.setupInterceptors();
  }

  /**
   * Setup all interceptors for the axios instance
   */
  private setupInterceptors(): void {
    // Request interceptors (order matters - applied in reverse order)
    this.axiosInstance.interceptors.request.use(
      localeInterceptor.onRequest.bind(localeInterceptor),
      localeInterceptor.onRequestError.bind(localeInterceptor)
    );

    this.axiosInstance.interceptors.request.use(
      authInterceptor.onRequest.bind(authInterceptor),
      authInterceptor.onRequestError.bind(authInterceptor)
    );

    // Response interceptors
    this.axiosInstance.interceptors.response.use(
      errorInterceptor.onResponse.bind(errorInterceptor),
      errorInterceptor.onResponseError.bind(errorInterceptor)
    );
  }

  /**
   * HTTP GET method
   */
  async get<T>(
    url: string,
    params?: object,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, {
      params,
      ...config,
    });
    return response.data;
  }

  /**
   * HTTP POST method
   */
  async post<T>(
    url: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP PUT method
   */
  async put<T>(
    url: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP PATCH method
   */
  async patch<T>(
    url: string,
    data?: object,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP DELETE method
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  /**
   * Get the underlying axios instance for advanced usage
   * Use sparingly and prefer the typed methods above
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

/**
 * Create a default HTTP client instance
 * Maintains compatibility with existing api object from api-client.ts
 */
export function createHttpClient(
  config?: Partial<HttpClientConfig>
): HttpClient {
  return new BaseHttpClient(config);
}

/**
 * Default HTTP client instance for backward compatibility
 * Replicates the 'api' object from original api-client.ts
 */
export const defaultHttpClient = createHttpClient();
